import {
  ArrowLeft, Building2, CalendarDays, FileDown, FileSpreadsheet, FileText, MapPin,
  Printer, Route,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { obter, paraQuery } from '../api';
import { Carregando, Erro, Vazio } from '../componentes/base';
import {
  exportarDocumentoEmpresa, exportarDocumentoFuncionario,
  exportarFolhaExcel, exportarFolhaPdf, exportarPontoExcel, exportarPontoPdf,
} from '../exportar';
import {
  dataCurta, euros, horas, km, mesPorExtenso, nomeDiaSemana, nomeMes, numero,
} from '../formatos';
import type {
  DiaFolha, Funcionario, LinhaEmpresa, LinhaFolha, LinhaFuncionarioEmpresa,
  Referencia, Registo, Taxas, Totais,
} from '../tipos';

type Vista = 'ponto' | 'funcionarios' | 'empresas';

const VISTAS: [Vista, string][] = [
  ['ponto', 'Folha de ponto'],
  ['funcionarios', 'Por funcionário'],
  ['empresas', 'Por fornecedor'],
];

export default function FolhaMensal() {
  const [mes, setMes] = useState('');
  const [vista, setVista] = useState<Vista>('ponto');
  const [selecionado, setSelecionado] = useState<{ id: string; empresaId?: string } | null>(null);
  const [empresaAberta, setEmpresaAberta] = useState<string | null>(null);
  const [referencia, setReferencia] = useState<Referencia | null>(null);

  // Same reason as the other screens: open on the latest month with data.
  useEffect(() => {
    obter<Referencia>('/referencia')
      .then((r) => { setReferencia(r); setMes((atual) => atual || r.ultimo_mes); })
      .catch(() => setMes(new Date().toISOString().slice(0, 7)));
  }, []);

  if (!mes) return <Carregando />;

  if (selecionado) {
    return (
      <DetalheFuncionario
        mes={mes}
        funcionarioId={selecionado.id}
        empresaId={selecionado.empresaId}
        valorKm={referencia?.valor_km ?? null}
        aoVoltar={() => setSelecionado(null)}
      />
    );
  }

  if (empresaAberta) {
    return (
      <DetalheEmpresa
        mes={mes} empresaId={empresaAberta}
        valorKm={referencia?.valor_km ?? null}
        aoVoltar={() => setEmpresaAberta(null)}
        aoAbrirFuncionario={(id) => { setEmpresaAberta(null); setSelecionado({ id, empresaId: empresaAberta }); }}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <CalendarDays className="h-6 w-6 text-marca-600 dark:text-marca-400" aria-hidden />
          <div>
            <h1 className="text-xl font-semibold">Folha de Ponto Mensal</h1>
            <p className="text-sm text-tinta-500 dark:text-tinta-400">{mesPorExtenso(mes)}</p>
          </div>
        </div>

        {/* One month, three readings: the sheet as it is checked, what each
            person is owed, and what each supplier billed. */}
        <div
          className="flex rounded-lg border border-papel-300 p-0.5 sem-impressao dark:border-tinta-700"
          role="tablist" aria-label="Vista"
        >
          {VISTAS.map(([chave, rotulo]) => (
            <button
              key={chave} type="button" role="tab" aria-selected={vista === chave}
              onClick={() => setVista(chave)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                vista === chave
                  ? 'bg-marca-600 text-white'
                  : 'text-tinta-600 hover:bg-papel-100 dark:text-papel-300 dark:hover:bg-tinta-700'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>
      </div>

      {vista === 'ponto' && (
        <FolhaPonto mes={mes} aoMudarMes={setMes} referencia={referencia} />
      )}
      {vista === 'funcionarios' && (
        <PorFuncionario
          mes={mes} aoMudarMes={setMes}
          aoAbrir={(id, empresaId) => setSelecionado({ id, empresaId })}
        />
      )}
      {vista === 'empresas' && (
        <PorEmpresa mes={mes} aoMudarMes={setMes} aoAbrir={setEmpresaAberta} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Month picker shared by the three sections. */
function SeletorMes({ mes, aoMudarMes, children }: {
  mes: string; aoMudarMes: (m: string) => void; children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 sem-impressao">
      <label htmlFor="folha-mes" className="sr-only">Mês</label>
      <input
        id="folha-mes" type="month" className="campo w-auto" value={mes}
        onChange={(e) => e.target.value && aoMudarMes(e.target.value)}
      />
      {children}
    </div>
  );
}

function Cartoes({ itens }: { itens: { rotulo: string; valor: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      {itens.map((item) => (
        <div key={item.rotulo} className="cartao p-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-tinta-500 dark:text-tinta-400">
            {item.rotulo}
          </p>
          <p className="mt-1 text-lg font-semibold tabular-nums">{item.valor}</p>
        </div>
      ))}
    </div>
  );
}

const BotaoDocumento = ({ aoClicar, rotulo }: { aoClicar: () => void; rotulo: string }) => (
  <button
    type="button"
    className="inline-flex items-center gap-1 rounded-lg border border-papel-300 px-2 py-1 text-xs
               font-medium text-tinta-600 hover:bg-papel-100 sem-impressao
               dark:border-tinta-700 dark:text-papel-300 dark:hover:bg-tinta-700"
    onClick={(e) => { e.stopPropagation(); aoClicar(); }}
    aria-label={rotulo}
  >
    <FileText className="h-3.5 w-3.5" aria-hidden />Documento
  </button>
);

// ---------------------------------------------------------------------------

interface RespostaPonto {
  registos: Registo[];
  totais: Totais;
  intervalo_ativo: boolean;
}

const FILTROS_PONTO = {
  empresa_id: '',
  funcao: '',
  funcionario_id: '',
  numero_servico: '',
  data_inicio: '',
  data_fim: '',
};

/**
 * The sheet itself: one row per shift, in the order the paper rota is read.
 *
 * This is the view that gets checked line by line against what the venue
 * signed off, so it stays flat — no grouping, no drill-down, every filter the
 * question could need sitting above the table.
 */
function FolhaPonto({ mes, aoMudarMes, referencia }: {
  mes: string;
  aoMudarMes: (mes: string) => void;
  referencia: Referencia | null;
}) {
  const [filtros, setFiltros] = useState(FILTROS_PONTO);
  const [resposta, setResposta] = useState<RespostaPonto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const [ano, mesNum] = mes.split('-');
  const intervaloAtivo = Boolean(filtros.data_inicio || filtros.data_fim);

  const carregar = useCallback(() => {
    setACarregar(true);
    setErro(null);
    obter<RespostaPonto>(`/registos${paraQuery({
      ...filtros,
      // A date range replaces ano/mês on the API, so sending both would be
      // contradictory rather than narrower.
      ano: intervaloAtivo ? undefined : ano,
      mes: intervaloAtivo ? undefined : String(Number(mesNum)),
      limite: 1000,
    })}`)
      .then(setResposta)
      .catch((e) => setErro(e.message))
      .finally(() => setACarregar(false));
  }, [filtros, ano, mesNum, intervaloAtivo]);

  useEffect(carregar, [carregar]);

  const descricao = useMemo(() => (
    intervaloAtivo
      ? `${filtros.data_inicio || 'início'} a ${filtros.data_fim || 'hoje'}`
      : mesPorExtenso(mes)
  ), [intervaloAtivo, filtros.data_inicio, filtros.data_fim, mes]);

  const registos = resposta?.registos ?? [];
  const filtrosAtivos = Object.values(filtros).filter(Boolean).length;

  function mudarMes(novoAno: string, novoMes: string) {
    aoMudarMes(`${novoAno}-${String(Number(novoMes)).padStart(2, '0')}`);
  }

  return (
    <div className="space-y-5">
      <section className="cartao p-4 sem-impressao" aria-label="Filtros">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label htmlFor="p-empresa" className="rotulo">Fornecedor</label>
            <select
              id="p-empresa" className="campo" value={filtros.empresa_id}
              onChange={(e) => setFiltros((f) => ({ ...f, empresa_id: e.target.value }))}
            >
              <option value="">Todos os fornecedores</option>
              {referencia?.empresas.map((e) => (
                <option key={e.id} value={e.id}>{e.nome}</option>
              ))}
              <option value="sem-empresa">— Sem fornecedor —</option>
            </select>
          </div>

          <div>
            <label htmlFor="p-funcao" className="rotulo">Função</label>
            <select
              id="p-funcao" className="campo" value={filtros.funcao}
              onChange={(e) => setFiltros((f) => ({ ...f, funcao: e.target.value }))}
            >
              <option value="">Todas as funções</option>
              {referencia?.funcoes.map((f) => (
                <option key={f.codigo} value={f.codigo}>{f.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="p-funcionario" className="rotulo">Funcionário</label>
            <select
              id="p-funcionario" className="campo" value={filtros.funcionario_id}
              onChange={(e) => setFiltros((f) => ({ ...f, funcionario_id: e.target.value }))}
            >
              <option value="">Todos os funcionários</option>
              {referencia?.funcionarios.map((f) => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="p-mes" className="rotulo">Mês</label>
            <select
              id="p-mes" className="campo" value={String(Number(mesNum))} disabled={intervaloAtivo}
              onChange={(e) => mudarMes(ano, e.target.value)}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{nomeMes(m)}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="p-ano" className="rotulo">Ano</label>
            <select
              id="p-ano" className="campo" value={ano} disabled={intervaloAtivo}
              onChange={(e) => mudarMes(e.target.value, mesNum)}
            >
              {Array.from(new Set([
                ...(referencia?.anos ?? []),
                Number(ano),
                new Date().getFullYear(),
              ])).sort((a, b) => b - a).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="p-servico" className="rotulo">Nº Serviço</label>
            <input
              id="p-servico" className="campo" placeholder="Ex.: 20260715-2"
              value={filtros.numero_servico}
              onChange={(e) => setFiltros((f) => ({ ...f, numero_servico: e.target.value }))}
            />
          </div>
        </div>

        <fieldset className="mt-4 border-t border-papel-200 pt-4 dark:border-tinta-700">
          <legend className="sr-only">Filtro por intervalo de datas</legend>
          <p className="mb-2 text-sm font-medium text-tinta-700 dark:text-papel-300">
            Filtro por Intervalo de Datas
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label htmlFor="p-inicio" className="rotulo">Data Início</label>
              <input
                id="p-inicio" type="date" className="campo" value={filtros.data_inicio}
                onChange={(e) => setFiltros((f) => ({ ...f, data_inicio: e.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="p-fim" className="rotulo">Data Fim</label>
              <input
                id="p-fim" type="date" className="campo" value={filtros.data_fim}
                onChange={(e) => setFiltros((f) => ({ ...f, data_fim: e.target.value }))}
              />
            </div>
          </div>
          <p className="mt-1 text-xs text-tinta-500 dark:text-tinta-400">
            Nota: o filtro por intervalo de datas substitui os filtros de mês/ano quando ativo.
          </p>
        </fieldset>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          {filtrosAtivos > 0 ? (
            <button
              type="button"
              className="text-sm font-medium text-marca-600 hover:underline dark:text-marca-400"
              onClick={() => setFiltros(FILTROS_PONTO)}
            >
              Limpar filtros ({filtrosAtivos})
            </button>
          ) : <span />}
          <div className="flex flex-wrap gap-2">
            <button
              type="button" className="btn-secundario" onClick={() => window.print()}
              disabled={!registos.length}
            >
              <Printer className="h-4 w-4" aria-hidden />Imprimir
            </button>
            <button
              type="button" className="btn bg-emerald-600 text-white hover:bg-emerald-700"
              disabled={!registos.length}
              onClick={() => resposta && exportarPontoExcel(registos, resposta.totais, descricao)}
            >
              <FileSpreadsheet className="h-4 w-4" aria-hidden />Exportar Excel
            </button>
            <button
              type="button" className="btn bg-red-600 text-white hover:bg-red-700"
              disabled={!registos.length}
              onClick={() => resposta && exportarPontoPdf(registos, resposta.totais, descricao)}
            >
              <FileDown className="h-4 w-4" aria-hidden />Exportar PDF
            </button>
          </div>
        </div>
      </section>

      {erro && <Erro mensagem={erro} aoTentar={carregar} />}

      {resposta && registos.length > 0 && (
        <Cartoes itens={[
          { rotulo: 'Registos', valor: numero(resposta.totais.n_registos) },
          { rotulo: 'Funcionários', valor: numero(resposta.totais.n_funcionarios) },
          { rotulo: 'Total de horas', valor: horas(resposta.totais.total_horas) },
          { rotulo: 'Km', valor: km(resposta.totais.total_km) },
          { rotulo: 'Total a pagar', valor: euros(resposta.totais.custo_total) },
        ]} />
      )}

      <div className="cartao overflow-hidden">
        {aCarregar ? (
          <Carregando />
        ) : !registos.length ? (
          <Vazio
            titulo="Sem registos para estes filtros"
            descricao="Escolha outro mês ou limpe os filtros."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-papel-50 text-left text-[11px] uppercase tracking-wide text-tinta-500 dark:bg-tinta-700/50 dark:text-tinta-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Data</th>
                  <th className="px-4 py-3 font-medium">Início</th>
                  <th className="px-4 py-3 font-medium">Fim</th>
                  <th className="px-4 py-3 text-right font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Local</th>
                  <th className="px-4 py-3 text-right font-medium">Km</th>
                  <th className="px-4 py-3 font-medium">Nº Serviço</th>
                  <th className="px-4 py-3 font-medium">Funcionário</th>
                  <th className="px-4 py-3 font-medium">Fornecedor</th>
                  <th className="px-4 py-3 text-right font-medium">A pagar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-papel-200 dark:divide-tinta-700">
                {registos.map((r) => (
                  <tr key={r.id} className="hover:bg-papel-50 dark:hover:bg-tinta-700/40">
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums">
                      {dataCurta(r.data, true)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-tinta-600 dark:text-papel-300">
                      {r.hora_inicio}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-tinta-600 dark:text-papel-300">
                      {r.hora_fim}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                      {horas(r.total_horas)}
                    </td>
                    <td className="px-4 py-2.5 text-tinta-600 dark:text-papel-300">
                      {r.local_nome ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 shrink-0 text-tinta-400" aria-hidden />
                          {r.local_nome}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-tinta-600 dark:text-papel-300">
                      <span className="inline-flex items-center gap-1">
                        <Route className="h-3.5 w-3.5 shrink-0 text-tinta-400" aria-hidden />
                        {km(r.km)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-tinta-600 dark:text-papel-300">
                      {r.numero_servico ?? <span className="text-tinta-400">sem nº</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium">{r.funcionario_nome}</p>
                      <p className="text-xs text-tinta-500 dark:text-tinta-400">
                        {r.funcao_nome ?? r.funcao}
                        {r.custo_hora_efetivo !== null && ` · ${euros(r.custo_hora_efetivo)}/h`}
                      </p>
                    </td>
                    <td className="px-4 py-2.5">
                      {r.empresa_nome ? (
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          r.empresa_tipo === 'interno'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : 'bg-papel-200 text-tinta-700 dark:bg-tinta-700 dark:text-papel-300'
                        }`}>
                          {r.empresa_nome}
                        </span>
                      ) : <span className="text-tinta-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {euros(r.custo_total)}
                      {Boolean(r.valor_km_total) && (
                        <p className="text-xs font-normal text-tinta-500 dark:text-tinta-400">
                          inc. {euros(r.valor_km_total)} km
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {resposta && (
                <tfoot className="border-t-2 border-marca-600 bg-papel-50 font-semibold dark:bg-tinta-700/50">
                  <tr>
                    <td className="px-4 py-3" colSpan={3}>Total</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {horas(resposta.totais.total_horas)}
                    </td>
                    <td className="px-4 py-3" />
                    <td className="px-4 py-3 text-right tabular-nums">
                      {km(resposta.totais.total_km)}
                    </td>
                    <td className="px-4 py-3" colSpan={3}>
                      <span className="font-normal text-tinta-500 dark:text-tinta-400">
                        {numero(resposta.totais.n_registos)} registo(s) ·{' '}
                        {numero(resposta.totais.n_funcionarios)} funcionário(s)
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {euros(resposta.totais.custo_total)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** What each person is owed for the month, one line per person per empresa. */
function PorFuncionario({ mes, aoMudarMes, aoAbrir }: {
  mes: string;
  aoMudarMes: (mes: string) => void;
  aoAbrir: (id: string, empresaId?: string) => void;
}) {
  const [linhas, setLinhas] = useState<LinhaFolha[]>([]);
  const [totais, setTotais] = useState<Totais | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const carregar = useCallback(() => {
    setACarregar(true);
    setErro(null);
    obter<{ linhas: LinhaFolha[]; totais: Totais }>(`/folha-mensal${paraQuery({ mes })}`)
      .then((r) => { setLinhas(r.linhas); setTotais(r.totais); })
      .catch((e) => setErro(e.message))
      .finally(() => setACarregar(false));
  }, [mes]);

  useEffect(carregar, [carregar]);

  return (
    <div className="space-y-5">
      <SeletorMes mes={mes} aoMudarMes={aoMudarMes}>
        <button
          type="button" className="btn bg-emerald-600 text-white hover:bg-emerald-700"
          disabled={!linhas.length} onClick={() => exportarFolhaExcel(linhas, mes)}
        >
          <FileSpreadsheet className="h-4 w-4" aria-hidden />Excel
        </button>
        <button
          type="button" className="btn bg-red-600 text-white hover:bg-red-700"
          disabled={!linhas.length} onClick={() => exportarFolhaPdf(linhas, mes)}
        >
          <FileDown className="h-4 w-4" aria-hidden />PDF
        </button>
      </SeletorMes>

      {erro && <Erro mensagem={erro} aoTentar={carregar} />}

      {totais && linhas.length > 0 && (
        <Cartoes itens={[
          { rotulo: 'Funcionários', valor: numero(totais.n_funcionarios) },
          { rotulo: 'Total de horas', valor: horas(totais.total_horas) },
          { rotulo: 'Valor das horas', valor: euros(totais.valor_horas) },
          { rotulo: 'Valor dos km', valor: euros(totais.valor_km) },
          { rotulo: 'Total a pagar', valor: euros(totais.custo_total) },
        ]} />
      )}

      <div className="cartao overflow-hidden">
        {aCarregar ? (
          <Carregando />
        ) : !linhas.length ? (
          <Vazio
            titulo="Sem registos neste mês"
            descricao="Escolha outro mês ou registe horas primeiro."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-papel-50 text-left text-[11px] uppercase tracking-wide text-tinta-500 dark:bg-tinta-700/50 dark:text-tinta-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Funcionário</th>
                  <th className="px-4 py-3 font-medium">Fornecedor</th>
                  <th className="px-4 py-3 text-right font-medium">Dias</th>
                  <th className="px-4 py-3 text-right font-medium">Horas</th>
                  <th className="px-4 py-3 text-right font-medium">Km</th>
                  <th className="px-4 py-3 text-right font-medium">Valor horas</th>
                  <th className="px-4 py-3 text-right font-medium">Valor km</th>
                  <th className="px-4 py-3 text-right font-medium">A receber</th>
                  <th className="px-4 py-3 text-right font-medium sem-impressao">Documento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-papel-200 dark:divide-tinta-700">
                {linhas.map((l) => (
                  <tr
                    key={`${l.funcionario_id}-${l.empresa_id ?? 'sem'}`}
                    className="cursor-pointer hover:bg-papel-50 dark:hover:bg-tinta-700/40"
                    onClick={() => aoAbrir(l.funcionario_id, l.empresa_id ?? undefined)}
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-left font-medium text-marca-700 hover:underline dark:text-marca-300"
                      >
                        {l.funcionario_nome}
                      </button>
                      <p className="text-xs text-tinta-500 dark:text-tinta-400">{l.funcao_nome}</p>
                    </td>
                    <td className="px-4 py-3 text-tinta-600 dark:text-papel-300">
                      {l.empresa_nome ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{l.n_dias}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">{horas(l.total_horas)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{km(l.total_km)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(l.valor_horas)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(l.valor_km)}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">{euros(l.custo_total)}</td>
                    <td className="px-4 py-3 text-right">
                      <BotaoDocumento
                        rotulo={`Documento de ${l.funcionario_nome}`}
                        aoClicar={() => aoAbrir(l.funcionario_id, l.empresa_id ?? undefined)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              {totais && (
                <tfoot className="border-t-2 border-marca-600 bg-papel-50 font-semibold dark:bg-tinta-700/50">
                  <tr>
                    <td className="px-4 py-3" colSpan={3}>Total</td>
                    <td className="px-4 py-3 text-right tabular-nums">{horas(totais.total_horas)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{km(totais.total_km)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(totais.valor_horas)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(totais.valor_km)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(totais.custo_total)}</td>
                    <td className="px-4 py-3" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** What each supplier billed. The view an agency invoice is checked against. */
function PorEmpresa({ mes, aoMudarMes, aoAbrir }: {
  mes: string;
  aoMudarMes: (mes: string) => void;
  aoAbrir: (empresaId: string) => void;
}) {
  const [linhas, setLinhas] = useState<LinhaEmpresa[]>([]);
  const [totais, setTotais] = useState<Totais | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const carregar = useCallback(() => {
    setACarregar(true);
    setErro(null);
    obter<{ linhas: LinhaEmpresa[]; totais: Totais }>(`/folha-mensal/empresas${paraQuery({ mes })}`)
      .then((r) => { setLinhas(r.linhas); setTotais(r.totais); })
      .catch((e) => setErro(e.message))
      .finally(() => setACarregar(false));
  }, [mes]);

  useEffect(carregar, [carregar]);

  const total = totais?.custo_total ?? 0;

  return (
    <div className="space-y-5">
      <SeletorMes mes={mes} aoMudarMes={aoMudarMes} />

      {erro && <Erro mensagem={erro} aoTentar={carregar} />}

      {totais && linhas.length > 0 && (
        <Cartoes itens={[
          { rotulo: 'Fornecedores', valor: numero(linhas.length) },
          { rotulo: 'Funcionários', valor: numero(totais.n_funcionarios) },
          { rotulo: 'Total de horas', valor: horas(totais.total_horas) },
          { rotulo: 'Km', valor: km(totais.total_km) },
          { rotulo: 'Total a pagar', valor: euros(totais.custo_total) },
        ]} />
      )}

      <div className="cartao overflow-hidden">
        {aCarregar ? (
          <Carregando />
        ) : !linhas.length ? (
          <Vazio titulo="Sem registos neste mês" descricao="Escolha outro mês." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-papel-50 text-left text-[11px] uppercase tracking-wide text-tinta-500 dark:bg-tinta-700/50 dark:text-tinta-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Fornecedor</th>
                  <th className="px-4 py-3 text-right font-medium">Funcionários</th>
                  <th className="px-4 py-3 text-right font-medium">Serviços</th>
                  <th className="px-4 py-3 text-right font-medium">Horas</th>
                  <th className="px-4 py-3 text-right font-medium">Km</th>
                  <th className="px-4 py-3 text-right font-medium">Valor horas</th>
                  <th className="px-4 py-3 text-right font-medium">Valor km</th>
                  <th className="px-4 py-3 text-right font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Peso</th>
                  <th className="px-4 py-3 text-right font-medium sem-impressao">Documento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-papel-200 dark:divide-tinta-700">
                {linhas.map((l) => {
                  const chave = l.empresa_id ?? 'sem-empresa';
                  const peso = total > 0 ? ((l.custo_total ?? 0) / total) * 100 : 0;
                  return (
                    <tr
                      key={chave}
                      className="cursor-pointer hover:bg-papel-50 dark:hover:bg-tinta-700/40"
                      onClick={() => aoAbrir(chave)}
                    >
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          className="flex items-center gap-2 text-left font-medium text-marca-700 hover:underline dark:text-marca-300"
                        >
                          <Building2 className="h-4 w-4 shrink-0 text-tinta-400" aria-hidden />
                          {l.empresa_nome}
                        </button>
                        <p className="ml-6 text-xs text-tinta-500 dark:text-tinta-400">
                          {l.empresa_tipo === 'interno' ? 'Interno' : l.empresa_tipo === 'agencia' ? 'Agência' : '—'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{l.n_funcionarios}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{l.n_servicos}</td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">{horas(l.total_horas)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{km(l.total_km)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{euros(l.valor_horas)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{euros(l.valor_km)}</td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">{euros(l.custo_total)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-papel-200 dark:bg-tinta-700">
                            <div
                              className="h-full rounded-full bg-marca-600"
                              style={{ width: `${Math.max(peso, 1)}%` }}
                            />
                          </div>
                          <span className="text-xs tabular-nums text-tinta-500 dark:text-tinta-400">
                            {peso.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <BotaoDocumento
                          rotulo={`Documento de ${l.empresa_nome}`}
                          aoClicar={() => aoAbrir(chave)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {totais && (
                <tfoot className="border-t-2 border-marca-600 bg-papel-50 font-semibold dark:bg-tinta-700/50">
                  <tr>
                    <td className="px-4 py-3">Total</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {numero(totais.n_funcionarios)}
                      {/* Deliberately not the column sum: someone who worked
                          for two suppliers is counted once here and once in
                          each of their rows. */}
                      <p className="text-xs font-normal text-tinta-500 dark:text-tinta-400">
                        pessoas distintas
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{numero(totais.n_servicos)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{horas(totais.total_horas)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{km(totais.total_km)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(totais.valor_horas)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(totais.valor_km)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{euros(totais.custo_total)}</td>
                    <td className="px-4 py-3" colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DetalheEmpresa({ mes, empresaId, valorKm, aoVoltar, aoAbrirFuncionario }: {
  mes: string;
  empresaId: string;
  valorKm: number | null;
  aoVoltar: () => void;
  aoAbrirFuncionario: (id: string) => void;
}) {
  const [dados, setDados] = useState<{
    empresa: { id: string; nome: string; tipo: string | null };
    registos: Registo[];
    por_funcionario: LinhaFuncionarioEmpresa[];
    resumo: Totais;
  } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const carregar = useCallback(() => {
    setACarregar(true);
    obter<NonNullable<typeof dados>>(`/folha-mensal/empresa/${empresaId}${paraQuery({ mes })}`)
      .then(setDados)
      .catch((e) => setErro(e.message))
      .finally(() => setACarregar(false));
  }, [empresaId, mes]);

  useEffect(carregar, [carregar]);

  if (aCarregar) return <Carregando />;
  if (erro) return <Erro mensagem={erro} aoTentar={carregar} />;
  if (!dados) return null;

  const { empresa, registos, por_funcionario: porFuncionario, resumo } = dados;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button" onClick={aoVoltar}
            className="sem-impressao rounded-lg p-2 text-tinta-500 hover:bg-papel-200 dark:hover:bg-tinta-700"
            aria-label="Voltar aos fornecedores"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </button>
          <div>
            <h1 className="text-xl font-semibold">{empresa.nome}</h1>
            <p className="text-sm text-tinta-500 dark:text-tinta-400">
              {empresa.tipo === 'interno' ? 'Interno' : empresa.tipo === 'agencia' ? 'Agência' : 'Sem fornecedor'}
              {' · '}{mesPorExtenso(mes)}
            </p>
          </div>
        </div>
        <button
          type="button" className="btn-primario sem-impressao"
          onClick={() => exportarDocumentoEmpresa({
            mes,
            empresa: empresa.nome,
            tipo: empresa.tipo,
            porFuncionario,
            registos,
            totalHoras: resumo.total_horas,
            totalKm: resumo.total_km,
            valorHoras: resumo.valor_horas ?? 0,
            valorKm: resumo.valor_km ?? 0,
            total: resumo.custo_total ?? 0,
            valorKmUnitario: valorKm,
          })}
        >
          <FileText className="h-4 w-4" aria-hidden />Gerar documento
        </button>
      </div>

      <Cartoes itens={[
        { rotulo: 'Funcionários', valor: numero(porFuncionario.length) },
        { rotulo: 'Total de horas', valor: horas(resumo.total_horas) },
        { rotulo: 'Km', valor: km(resumo.total_km) },
        { rotulo: 'Valor das horas', valor: euros(resumo.valor_horas) },
        { rotulo: 'Total a receber', valor: euros(resumo.custo_total) },
      ]} />

      <div className="cartao overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-papel-50 text-left text-[11px] uppercase tracking-wide text-tinta-500 dark:bg-tinta-700/50 dark:text-tinta-400">
              <tr>
                <th className="px-4 py-3 font-medium">Funcionário</th>
                <th className="px-4 py-3 text-right font-medium">Dias</th>
                <th className="px-4 py-3 text-right font-medium">Horas</th>
                <th className="px-4 py-3 text-right font-medium">Km</th>
                <th className="px-4 py-3 text-right font-medium">€/h</th>
                <th className="px-4 py-3 text-right font-medium">Valor horas</th>
                <th className="px-4 py-3 text-right font-medium">Valor km</th>
                <th className="px-4 py-3 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-papel-200 dark:divide-tinta-700">
              {porFuncionario.map((l) => (
                <tr
                  key={l.funcionario_id}
                  className="cursor-pointer hover:bg-papel-50 dark:hover:bg-tinta-700/40"
                  onClick={() => aoAbrirFuncionario(l.funcionario_id)}
                >
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="text-left font-medium text-marca-700 hover:underline dark:text-marca-300"
                    >
                      {l.funcionario_nome}
                    </button>
                    <p className="text-xs text-tinta-500 dark:text-tinta-400">{l.funcao_nome}</p>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{l.n_dias}</td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">{horas(l.total_horas)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{km(l.total_km)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {l.valor_hora_min === null ? '—'
                      : Math.abs((l.valor_hora_max ?? l.valor_hora_min) - l.valor_hora_min) < 0.005
                        ? euros(l.valor_hora_min)
                        : `${euros(l.valor_hora_min)}–${euros(l.valor_hora_max)}`}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{euros(l.valor_horas)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{euros(l.valor_km)}</td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">{euros(l.custo_total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-marca-600 bg-papel-50 font-semibold dark:bg-tinta-700/50">
              <tr>
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right tabular-nums">{numero(resumo.n_dias)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{horas(resumo.total_horas)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{km(resumo.total_km)}</td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-right tabular-nums">{euros(resumo.valor_horas)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{euros(resumo.valor_km)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{euros(resumo.custo_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DetalheFuncionario({ mes, funcionarioId, empresaId, valorKm, aoVoltar }: {
  mes: string;
  funcionarioId: string;
  empresaId?: string;
  valorKm: number | null;
  aoVoltar: () => void;
}) {
  const [dados, setDados] = useState<{
    funcionario: Funcionario; dias: DiaFolha[]; resumo: Totais; taxas: Taxas;
  } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const carregar = useCallback(() => {
    setACarregar(true);
    obter<NonNullable<typeof dados>>(
      `/folha-mensal/${funcionarioId}${paraQuery({ mes, empresa_id: empresaId })}`,
    )
      .then(setDados)
      .catch((e) => setErro(e.message))
      .finally(() => setACarregar(false));
  }, [funcionarioId, mes, empresaId]);

  useEffect(carregar, [carregar]);

  if (aCarregar) return <Carregando />;
  if (erro) return <Erro mensagem={erro} aoTentar={carregar} />;
  if (!dados) return null;

  const { funcionario, dias, resumo, taxas } = dados;
  const trabalhados = dias.flatMap((d) => d.registos);
  // The empresa the document is for: the filter when one was applied,
  // otherwise whatever the shifts say.
  const empresaNome = empresaId
    ? trabalhados[0]?.empresa_nome ?? funcionario.empresa_nome ?? null
    : funcionario.empresa_nome ?? null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button" onClick={aoVoltar}
            className="sem-impressao rounded-lg p-2 text-tinta-500 hover:bg-papel-200 dark:hover:bg-tinta-700"
            aria-label="Voltar"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </button>
          <div>
            <h1 className="text-xl font-semibold">{funcionario.nome}</h1>
            <p className="text-sm text-tinta-500 dark:text-tinta-400">
              {funcionario.funcao_nome ?? funcionario.funcao}
              {empresaNome && ` · ${empresaNome}`}
              {' · '}{mesPorExtenso(mes)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sem-impressao">
          <button type="button" className="btn-secundario" onClick={() => window.print()}>
            <Printer className="h-4 w-4" aria-hidden />Imprimir
          </button>
          <button
            type="button" className="btn-primario"
            disabled={!trabalhados.length}
            onClick={() => exportarDocumentoFuncionario({
              mes,
              nome: funcionario.nome,
              funcao: funcionario.funcao_nome ?? funcionario.funcao,
              empresa: empresaNome,
              registos: trabalhados,
              totalHoras: resumo.total_horas,
              totalKm: resumo.total_km,
              valorHoras: resumo.valor_horas ?? 0,
              valorKm: resumo.valor_km ?? 0,
              total: resumo.custo_total ?? 0,
              valorHoraMin: taxas?.valor_hora_min ?? null,
              valorHoraMax: taxas?.valor_hora_max ?? null,
              valorKmUnitario: taxas?.valor_km_min ?? valorKm,
            })}
          >
            <FileText className="h-4 w-4" aria-hidden />Gerar documento
          </button>
        </div>
      </div>

      <Cartoes itens={[
        { rotulo: 'Dias trabalhados', valor: numero(resumo.n_dias) },
        { rotulo: 'Total de horas', valor: horas(resumo.total_horas) },
        { rotulo: 'Km', valor: km(resumo.total_km) },
        { rotulo: 'Valor das horas', valor: euros(resumo.valor_horas) },
        { rotulo: 'Total a receber', valor: euros(resumo.custo_total) },
      ]} />

      <div className="cartao overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-papel-50 text-left text-[11px] uppercase tracking-wide text-tinta-500 dark:bg-tinta-700/50 dark:text-tinta-400">
              <tr>
                <th className="px-4 py-3 font-medium">Dia</th>
                <th className="px-4 py-3 font-medium">Horário</th>
                <th className="px-4 py-3 font-medium">Local</th>
                <th className="px-4 py-3 font-medium">Nº Serviço</th>
                <th className="px-4 py-3 text-right font-medium">Km</th>
                <th className="px-4 py-3 text-right font-medium">Horas</th>
                <th className="px-4 py-3 text-right font-medium">A receber</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-papel-200 dark:divide-tinta-700">
              {dias.map((dia) => {
                const fimDeSemana = dia.dia_semana === 0 || dia.dia_semana === 6;
                if (!dia.registos.length) {
                  return (
                    <tr key={dia.data} className={fimDeSemana ? 'bg-papel-50/60 dark:bg-tinta-700/20' : ''}>
                      <td className="px-4 py-2 tabular-nums text-tinta-400">
                        {dataCurta(dia.data)}
                        <span className="ml-2 text-xs">{nomeDiaSemana(dia.dia_semana)}</span>
                      </td>
                      <td className="px-4 py-2 text-papel-300 dark:text-tinta-600" colSpan={6}>—</td>
                    </tr>
                  );
                }
                return dia.registos.map((r, indice) => (
                  <tr key={r.id} className={fimDeSemana ? 'bg-papel-50/60 dark:bg-tinta-700/20' : ''}>
                    {indice === 0 && (
                      <td className="px-4 py-2 align-top tabular-nums font-medium" rowSpan={dia.registos.length}>
                        {dataCurta(dia.data)}
                        <span className="ml-2 text-xs font-normal text-tinta-500">
                          {nomeDiaSemana(dia.dia_semana)}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-2 tabular-nums text-tinta-600 dark:text-papel-300">
                      {r.hora_inicio} – {r.hora_fim}
                      {r.pausa_minutos > 0 && (
                        <span className="ml-2 text-xs text-tinta-400">−{r.pausa_minutos} min</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-tinta-600 dark:text-papel-300">{r.local_nome ?? '—'}</td>
                    <td className="px-4 py-2 tabular-nums text-tinta-600 dark:text-papel-300">{r.numero_servico ?? '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-tinta-600 dark:text-papel-300">
                      {r.km ? km(r.km) : '—'}
                    </td>
                    <td className="px-4 py-2 text-right font-medium tabular-nums">
                      {horas(r.total_horas)}
                      {r.horas_extra > 0 && (
                        <span className="ml-1 text-xs font-normal text-orange-600 dark:text-orange-400">
                          (+{r.horas_extra})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{euros(r.custo_total)}</td>
                  </tr>
                ));
              })}
            </tbody>
            <tfoot className="border-t-2 border-marca-600 bg-papel-50 font-semibold dark:bg-tinta-700/50">
              <tr>
                <td className="px-4 py-3" colSpan={4}>Total do mês</td>
                <td className="px-4 py-3 text-right tabular-nums">{km(resumo.total_km)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{horas(resumo.total_horas)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{euros(resumo.custo_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
